<script setup lang="ts">
import { computed, reactive, watch, onBeforeMount } from "vue";
import { judgmentOptions, kdm, editMode, discussionState, getCommentIcon } from "@/store/kdm";
import { useDebounceFn } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  cellId: string,
  isLocked?: boolean
}>();

const emit = defineEmits(['update']);

const data = reactive({
  sliderValue: 0,
  judgmentId: '',
});

const bgColor = computed(() => {
  if (data.judgmentId) {
    const judgment = judgmentOptions.value.get(data.judgmentId);
    if (judgment) {
      return `var(--judgment-option-${data.judgmentId})`;
    }
  }
  return 'white';
});

const commentIcon = computed(() => getCommentIcon(props.cellId));

function openDiscussion() {
  discussionState.coords = props.cellId;
  discussionState.nodeId = kdm.value?.nid || '';
  discussionState.canWrite = !props.isLocked;
  discussionState.visible = true;
}

onBeforeMount(() => {
  const matrixData = kdm.value?.field_matrix_data;
  if (typeof matrixData === 'string') {
    let matrixDataObject = {};
    try { matrixDataObject = JSON.parse(matrixData); } catch (e) {}
    
    if (matrixDataObject && Object.hasOwn(matrixDataObject, props.cellId)) {
      const cellData = matrixDataObject[props.cellId];
      data.judgmentId = cellData.judgmentId || '';
      // Type 1 weight is implicit: a selected Evaluation Value always weighs 100%.
      data.sliderValue = data.judgmentId ? 100 : 0;
    }
  }
});

// Type 1 has no weight slider: selecting an Evaluation Value sets the weight to
// 100%, and an empty selection to 0%. This keeps sliderValue (used by
// computeCellWinner / the cell fill) consistent without user input.
watch(() => data.judgmentId, (id) => {
  data.sliderValue = id ? 100 : 0;
});

watch(data, useDebounceFn(() => {
  emit('update', { ...data });
}, 1000));

</script>

<template>
  <div class="simple-cell">
    <div class="d-flex align-items-center gap-2 mb-2">
      <select 
        :disabled="!editMode || isLocked" 
        v-model="data.judgmentId" 
        class="form-select form-select-sm py-0" 
        :style="`background-color: ${bgColor}; flex: 1;`"
      >
        <option value="">{{ t('epb.kdm.no_value') }}</option>
        <template v-for="[pid,judgment] in judgmentOptions">
          <option :value="pid">{{ judgment.title }}</option>
        </template>
      </select>
    </div>

    <div class="d-flex justify-content-end align-items-center mt-1">
        <button 
          type="button" 
          class="btn btn-sm btn-link text-decoration-none p-0" 
          @click.stop="openDiscussion"
          title="Discuss this cell"
        >
          <i class="bi" :class="[commentIcon.icon, commentIcon.class]"></i>
        </button>
    </div>
  </div>
</template>

<style scoped>
.simple-cell {
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>